import { useState } from "react";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Toolbar } from "@/components/ui/toolbar";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { useConfigView } from "@/hooks/use-config-view";
import { FileText, TriangleAlert } from "lucide-react";

// Must stay in step with ALL_MODULES in webapp/app/services/config_modules.py —
// /api/config/view rejects anything outside its module pattern with a 422 that
// renders here as "Failed to load config". `http` was offered but never
// generated by this app, and `hep` was generatable but unlistable, so hep.conf
// could not be inspected from the UI at all.
const MODULES = [
  { value: "pjsip", label: "PJSIP (pjsip.conf)" },
  { value: "extensions", label: "Extensions (extensions.conf)" },
  { value: "ari", label: "ARI (ari.conf)" },
  { value: "websocket", label: "WebSocket (websocket_client.conf)" },
  { value: "hep", label: "HEP (hep.conf)" },
];

export default function ConfigViewPage() {
  const [module, setModule] = useState("pjsip");
  const { data, isLoading, isError, refetch, isFetching } = useConfigView(module);

  return (
    <div className="space-y-4">
      <PageHeader title="Generated Config" description="View the raw Asterisk configuration files generated from the database." />

      <Toolbar
        filters={
          <Select
            value={module}
            onChange={(e) => setModule(e.target.value)}
            className="w-72"
          >
            {MODULES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        }
        onRefresh={() => refetch()}
        refreshing={isFetching}
      />

      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText size={14} className="text-muted-foreground" />
            {data?.path ?? MODULES.find((m) => m.value === module)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-8 text-sm text-muted-foreground text-center">
              Loading config...
            </div>
          )}
          {isError && (
            <EmptyState
              icon={<TriangleAlert size={24} />}
              title="Failed to load config"
              action={
                <Button variant="secondary" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              }
              className="rounded-sm border border-border bg-card"
            />
          )}
          {data && (
            <pre className="text-xs font-mono bg-muted/30 p-4 overflow-auto max-h-[calc(100vh-280px)] whitespace-pre">
              {data.content || (
                <span className="text-muted-foreground italic">(empty file)</span>
              )}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
